import "@progress/kendo-ui/js/kendo.autocomplete";
import "@progress/kendo-ui/js/kendo.datepicker";
import "@progress/kendo-ui/js/kendo.timepicker";
import * as ko from "knockout";
import * as moment from "moment";
import * as Constants from "../common/constants";
import * as Global from "../common/global";
import { TargetEditControl } from "../common/targetEdit";
import * as UI from "../common/ui";
import * as Util from "../common/util";
import * as AppointmentAddData from "../data/appointmentAddData";
import * as AppointmentData from "../data/appointmentData";
import * as TargetData from "../data/targetData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";
import { ILanguage } from "../interfaces/translations";
import { ICustomerSettings } from "../interfaces/user";

interface IViewModel {
    InspectorID: KnockoutObservable<number>;
    AppointmentDateString: KnockoutObservable<string>;
    TemplateID: KnockoutObservable<number>;
    IsTemplateIDMissing: KnockoutObservable<boolean>;
    TargetTemplateName: KnockoutObservable<string>;
    Timeframe: KnockoutObservable<number>;
    AppointmentDate: KnockoutObservable<string>;
    AppointmentTime: KnockoutObservable<string>;
    IsAppointmentDateMissing: KnockoutObservable<boolean>;
    IsAppointmentTimeMissing: KnockoutObservable<boolean>;
    IsAppointmentDateTimeInvalid: KnockoutObservable<boolean>;
    TargetTemplateID: KnockoutObservable<number>;
    TargetTemplateURLName: KnockoutObservable<string>;
    TargetTemplateTypeID: KnockoutObservable<number>;
    TargetID: KnockoutObservable<number>;
    IsTargetIDMissing: KnockoutObservable<boolean>;
    IsTargetPrepopulated: KnockoutObservable<boolean>;
    Templates: KnockoutObservableArray<IDropDownObservable>;
    Inspectors: KnockoutObservableArray<IDropDownObservable>;
    AppointmentLabel: KnockoutObservable<string>;
    InspectorLabel: KnockoutObservable<string>;
    TargetDataSource: kendo.data.DataSource | null;
    AddressFormItems: KnockoutObservableArray<ITargetTemplateFormItemObservable>;
    FormItems: KnockoutObservableArray<ITargetTemplateFormItemObservable>;
    Languages: KnockoutObservableArray<ILanguage>;
    DefaultLanguageID: KnockoutObservable<number>;
    templateSelected: (e: IViewModel) => void;
    onOpenAddTargetModal: () => void;
}

interface IAddTargetViewModel {
    closeAddTargetModal: () => void;
    saveNewTarget: () => void;
}

interface IDropDownObservable {
    ID: KnockoutObservable<number>;
    Name: KnockoutObservable<string>;
}

interface ITargetTemplateFormItemObservable {
    ID: KnockoutObservable<number>;
    FormText: KnockoutObservable<string>;
    IsRequired: KnockoutObservable<boolean>;
    MaxLength: KnockoutObservable<number>;
    Value: KnockoutObservable<string>;
    IsReadOnly: KnockoutObservable<boolean>;
    TargetTemplateFormItemID: KnockoutObservable<number>;

}

export class TaskAddController implements IController {
    private container: JQuery;
    private addTargetModal: JQuery;
    private targetEditControl: TargetEditControl;
    private viewModel: IViewModel;
    private addTargetViewModel: IAddTargetViewModel;
    private customerSettings: ICustomerSettings[];
    private DateFormat: string;
    private TimeFormat: string;
    private fieldsOnLoad: any = {};

    constructor() {
        const me: TaskAddController = this;
        const app: IApplication = Global.app();
        this.customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
        this.DateFormat = UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT);
        this.TimeFormat = UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT);

        this.viewModel = {
            InspectorID: ko.observable(0),
            AppointmentDateString: ko.observable(""),
            TemplateID: ko.observable(0),
            IsTemplateIDMissing: ko.observable(false),
            TargetTemplateName: ko.observable("Search Target"),
            Timeframe: ko.observable(0),
            AppointmentDate: ko.observable(""),
            AppointmentTime: ko.observable(""),
            IsAppointmentDateMissing: ko.observable(false),
            IsAppointmentTimeMissing: ko.observable(false),
            IsAppointmentDateTimeInvalid: ko.observable(false),
            TargetTemplateID: ko.observable(0),
            TargetTemplateTypeID: ko.observable(0),
            TargetID: ko.observable(0),
            IsTargetIDMissing: ko.observable(false),
            IsTargetPrepopulated: ko.observable(false),
            TargetTemplateURLName: ko.observable(""),
            Templates: ko.observableArray(),
            Inspectors: ko.observableArray(),
            AppointmentLabel: ko.observable(Constants.LABEL_APPOINTMENT),
            InspectorLabel: ko.observable(Constants.LABEL_INSPECTOR),
            TargetDataSource: AppointmentAddData.getTargetAutocompleteData((data: AppointmentAddData.IAutoCompleteParameters) => {
                data.TargetTemplateID = me.viewModel.TargetTemplateID();
                data.TargetTemplateTypeID = me.viewModel.TargetTemplateTypeID();
            }),
            AddressFormItems: ko.observableArray(),
            FormItems: ko.observableArray(),
            Languages: ko.observableArray<ILanguage>(),
            DefaultLanguageID: ko.observable(0),
            templateSelected: (e: IViewModel) => {
                me.setTargetTemplate(e.TemplateID());
            },
            onOpenAddTargetModal: () => {
                me.addTargetModal.modal("show");
                me.targetEditControl.load(me.viewModel.TargetTemplateURLName());
            },
        };

        this.addTargetViewModel = {
            closeAddTargetModal: () => {
                me.addTargetModal.modal("hide");
            },
            saveNewTarget: () => {
                me.targetEditControl.save().done((targetMaster) => {
                    me.addTargetModal.modal("hide");
                    me.updateTargetDataFromTargetMaster(targetMaster);
                });
            },
        };

        this.targetEditControl = new TargetEditControl();
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        this.addTargetModal = container.find("div[data-id='addTargetModal']");
        this.targetEditControl.render(container.find("div[data-id='targetEditControl']"));

        ko.applyBindings(this.viewModel, container.find("div[data-id='formContainer']")[0]);
        ko.applyBindings(this.addTargetViewModel, container.find("div[data-id='AddTargetButtons']")[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const me: TaskAddController = this;
        const momentDate: string = UI.getMomentDateFormat(me.DateFormat);
        const momentTime: string = UI.getMomentTimeFormat(me.TimeFormat);
        // if no date/time specified in URL, the use the current date/time
        let  defaultAppointmentDateTime = moment();
        if (Util.getURLParameter("date")) {
            defaultAppointmentDateTime = moment(Util.getURLParameter("date"));
        }
        me.viewModel.AppointmentDate(defaultAppointmentDateTime.format(momentDate));
        me.viewModel.AppointmentTime(defaultAppointmentDateTime.format(momentTime));

        me.container.find("input[data-id='appointmentDate']").kendoDatePicker({ format: UI.getKendoDateFormat(me.DateFormat) })
            .attr("placeholder", UI.getKendoDateFormat(me.DateFormat));
        me.container.find("input[data-id='appointmentTime']").kendoTimePicker({ format: UI.getKendoTimeFormat(me.TimeFormat), interval: 15 })
            .attr("placeholder", Constants.TIME_PLACEHOLDER);

        // initialize autocomplete
        me.container.find("input[data-id='searchTarget']").kendoAutoComplete({
            filter: "contains",
            separator: "\n ",
            minLength: 3,
            select: me.updateTarget.bind(me),
            dataSource: me.viewModel.TargetDataSource,
            template: kendo.template($("#template_appointmentAdd_autocomplete").html()),
            dataTextField: "Target",
        });

        return this.loadRecord();
    }

    public save(): JQueryPromise<any> {
        const me: TaskAddController = this;
        const app: IApplication = Global.app();
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        let createApptRequest: AppointmentAddData.ICreateAppointmentRequest;
        let apptDateTime: Date | null = null;

        if (!this.isValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            deferred.reject();
            return deferred.promise();
        }

        if (me.viewModel.AppointmentDate() && me.viewModel.AppointmentTime()) {
            const apptDateTimeMoment = me.getAppointmentDateTimeAsMoment();
            if (apptDateTimeMoment !== null) {
                apptDateTime = apptDateTimeMoment.toDate();
            }
        }

        createApptRequest = {
            AppointmentDate: apptDateTime,
            AssignedToUserID: me.viewModel.InspectorID(),
            TargetID: me.viewModel.TargetID(),
            TemplateID: me.viewModel.TemplateID(),
            StatusID: 1,
            Timeframe: me.viewModel.Timeframe(),
            LanguageID: me.viewModel.DefaultLanguageID(),
        };

        AppointmentAddData.createAppointment(createApptRequest)
            .done((response: Data.IResponse<AppointmentData.IAppointment>) => {
                if (response.Success) {
                    // add a new record, so navigate to the appointment URL
                    app.pageManager().gotoTaskPage(response.Payload.ID, true);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        waitContainer = UI.showWaitIndicator(me.container);

        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.viewModel);
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: any[]): string {
        let routerID: string = "Appointment";
        if (params != null && params.length === 1) {
            routerID += params[0];
        }
        return routerID;
    }

    public title(): string {
        return `Add ${Constants.LABEL_APPOINTMENT}`;
    }

    public subTitle(): string {
        return "";
    }
    private isValid(): boolean {
        const me: TaskAddController = this;
        let isValid: boolean = true;

        isValid = UI.setRequiredNumber(me.viewModel.TemplateID, me.viewModel.IsTemplateIDMissing) ? isValid : false;

        // Note that both Date and Time must be entered, or both fields must be empty.  If only one field has a value
        // then it is not valid.
        me.viewModel.IsAppointmentTimeMissing(false);
        me.viewModel.IsAppointmentDateMissing(false);
        me.viewModel.IsAppointmentDateTimeInvalid(false);

        if (me.viewModel.AppointmentDate()) {
            isValid = UI.setRequiredString(me.viewModel.AppointmentTime, me.viewModel.IsAppointmentTimeMissing) ? isValid : false;
        }

        if (me.viewModel.AppointmentTime()) {
            isValid = UI.setRequiredString(me.viewModel.AppointmentDate, me.viewModel.IsAppointmentDateMissing) ? isValid : false;
        }

        if (me.viewModel.AppointmentDate() && me.viewModel.AppointmentTime()) {
            const appointmentDateTimeMoment = this.getAppointmentDateTimeAsMoment();
            if (appointmentDateTimeMoment !== null && !appointmentDateTimeMoment.isValid()) {
                isValid = false;
                me.viewModel.IsAppointmentDateTimeInvalid(true);
            }
        }

        if (!me.viewModel.TargetID()) {
            me.viewModel.IsTargetIDMissing(true);
            isValid = false;
        }

        return isValid;
    }

    private loadRecord(): JQueryPromise<any> {
        const me: TaskAddController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        if (Util.getURLNumberParameter("targetID")) {
            me.viewModel.IsTargetPrepopulated(true);
        }

        const request: AppointmentAddData.IAppointmentLookupDataRequest = {
            TargetID: Util.getURLNumberParameter("targetID"),
        };

        AppointmentAddData.getAppointmentLookupData(request)
            .done((response: Data.IResponse<AppointmentAddData.ILookupData>) => {
                if (response.Success) {
                    me.updateApptViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);

                // remember the fields value so we can determine if they have changed
                me.fieldsOnLoad = ko.toJSON(this.viewModel);

                deferred.resolve();
            });

        return deferred.promise();
    }

    private updateApptViewModel(lookupData: AppointmentAddData.ILookupData): void {
        const me: TaskAddController = this;

        me.viewModel.Templates([]);
        me.viewModel.Inspectors([]);

        me.viewModel.Timeframe(lookupData.MajorTick);

        lookupData.Templates.forEach((template: Data.ICodeModel<number>) => {
            const tt: IDropDownObservable = {
                ID: ko.observable(template.ID),
                Name: ko.observable(template.Name),
            };

            me.viewModel.Templates.push(tt);
        });

        lookupData.Inspectors.forEach((inspector: Data.ICodeModel<number>) => {
            const ip: IDropDownObservable = {
                ID: ko.observable(inspector.ID),
                Name: ko.observable(inspector.Name),
            };

            me.viewModel.Inspectors.push(ip);
        });

        me.viewModel.InspectorID(Util.getURLNumberParameter("inspectorID"));

        if (lookupData.TargetTemplate) {
            me.updateTargetViewModel(lookupData.TargetTemplate);
            me.viewModel.TemplateID(Util.getURLNumberParameter("templateID"));
        }

        if (lookupData.Target) {
            me.updateTargetData(lookupData.Target);
            me.viewModel.IsTargetPrepopulated(true);
            me.viewModel.TargetID(Util.getURLNumberParameter("targetID"));
        }

        me.viewModel.Languages(lookupData.Languages);
        me.viewModel.DefaultLanguageID(lookupData.DefaultLanguageID);
    }

    private setTargetTemplate(templateID: number): JQuery.Promise<any, any, any> {
        const me: TaskAddController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (me.viewModel.IsTargetPrepopulated()) {
            deferred.resolve();
            return deferred.promise();
        }

        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        me.viewModel.AddressFormItems([]);
        me.viewModel.FormItems([]);

        me.viewModel.TargetID(0);
        me.container.find("input[data-id='searchTarget']").data("kendoAutoComplete").value("");

        if (!templateID) {
            UI.hideWaitIndicator(me.container, waitContainer);
            deferred.resolve();
            return deferred.promise();
        }

        AppointmentAddData.getTargetTemplate(templateID)
            .done((response: Data.IResponse<AppointmentAddData.ITargetTemplate>) => {
                if (response.Success) {
                    me.updateTargetViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private updateTargetViewModel(targetTemplate: AppointmentAddData.ITargetTemplate): void {
        const me: TaskAddController = this;

        me.viewModel.AddressFormItems([]);
        me.viewModel.FormItems([]);

        me.viewModel.IsTemplateIDMissing(false);
        me.viewModel.IsTargetIDMissing(false);
        me.viewModel.TargetID(0);
        me.viewModel.TargetTemplateTypeID(targetTemplate.TargetTemplateTypeID);
        me.viewModel.TargetTemplateName("Search " + targetTemplate.TemplateName);
        me.viewModel.TargetTemplateID(targetTemplate.ID);
        me.viewModel.TargetTemplateURLName(targetTemplate.URLFriendlyName);
        if (targetTemplate.TargetTemplateTypeID === TargetData.TARGETEMPLTETYPEID_LOCATION) {
            for (const address of targetTemplate.AddressFormItems) {
                if (address.FormText !== "Address 2" && address.FormText !== "County" && address.FormText !== "Country") {
                    const addr: ITargetTemplateFormItemObservable = {
                        ID: ko.observable(address.ID),
                        FormText: ko.observable(address.FormText),
                        IsRequired: ko.observable(address.IsRequired),
                        MaxLength: ko.observable(address.MaxLength),
                        Value: ko.observable(address.Value),
                        IsReadOnly: ko.observable(address.FormText !== "Address"),
                        TargetTemplateFormItemID: ko.observable(0),
                    };

                    me.viewModel.AddressFormItems.push(addr);
                }
            }
        } else if (targetTemplate.TargetTemplateTypeID === TargetData.TARGETEMPLTETYPEID_CUSTOM) {
            for (const item of targetTemplate.FormItems) {
                if (item.FormText && item.IsActive && item.ControlTypeID !== Constants.QuestionType.PICTURE) {
                    const itemOb: ITargetTemplateFormItemObservable = {
                        ID: ko.observable(item.ID),
                        FormText: ko.observable(item.FormText),
                        IsRequired: ko.observable(item.IsRequired),
                        MaxLength: ko.observable(item.MaxLength),
                        Value: ko.observable(item.Value),
                        IsReadOnly: ko.observable(true),
                        TargetTemplateFormItemID: ko.observable(0),
                    };

                    me.viewModel.FormItems.push(itemOb);
                }
            }
        }
    }

    private updateTarget(e: any): void {
        const me: TaskAddController = this;
        if (!e || !e.item || e.item.index() < 0) { return; }

        me.viewModel.IsTargetIDMissing(false);
        const targetSelected: AppointmentAddData.ITarget = e.sender.dataItem(e.item.index());
        me.updateTargetData(targetSelected);
    }

    private updateTargetData(targetSelected: AppointmentAddData.ITarget): void {
        const me: TaskAddController = this;

        me.viewModel.TargetID(targetSelected.TargetID);

        if (targetSelected.TargetTemplateTypeID === TargetData.TARGETEMPLTETYPEID_LOCATION) {
            me.viewModel.AddressFormItems()[0].Value(targetSelected.Address);
            me.viewModel.AddressFormItems()[1].Value(targetSelected.City);
            me.viewModel.AddressFormItems()[2].Value(targetSelected.State);
            me.viewModel.AddressFormItems()[3].Value(targetSelected.Zip);
            me.viewModel.AddressFormItems()[4].Value(targetSelected.Phone);

        } else if (targetSelected.TargetTemplateTypeID === TargetData.TARGETEMPLTETYPEID_CUSTOM) {
            for (const itemViewModel of me.viewModel.FormItems()) {
                itemViewModel.Value("");
                for (const fieldValue of targetSelected.FieldValues) {
                    if (itemViewModel.FormText() === fieldValue.Name) {
                        itemViewModel.Value(UI.formatValueFromControlType(fieldValue.Value, fieldValue.ControlTypeID));
                        break;
                    }
                }
            }
        }
    }

    private updateTargetDataFromTargetMaster(target: TargetData.ITargetMaster) {
        const me: TaskAddController = this;

        me.viewModel.TargetID(target.ID);

        if (target.HasAddressFields) {
            me.viewModel.AddressFormItems().forEach((item: ITargetTemplateFormItemObservable) => {
                target.AddressFormItems.forEach((formItem) => {
                    if (item.FormText() === formItem.FormText) {
                        item.Value(formItem.Value);
                    }
                });
            });
        } else {
            me.viewModel.FormItems().forEach((item: ITargetTemplateFormItemObservable) => {
                target.FormItems.forEach((formItem) => {
                    if (item.FormText() === formItem.FormText) {
                        item.Value(formItem.Value);
                    }
                });
            });
        }
    }

    private getAppointmentDateTimeAsMoment(): moment.Moment | null {
        if (this.viewModel.AppointmentDate() && this.viewModel.AppointmentTime()) {
            const momentFormat: string = UI.getMomentDateFormat(this.DateFormat) + " " + UI.getMomentTimeFormat(this.TimeFormat);
            return moment(this.viewModel.AppointmentDate() + " " + this.viewModel.AppointmentTime(), momentFormat);
        } else {
            return null;
        }
    }

}

function formatAutocompleteTarget(targetFieldValue: AppointmentAddData.ITargetFieldValue): string {
    return UI.formatValueFromControlType(targetFieldValue.Value, targetFieldValue.ControlTypeID);
}

const myGlobal = (window /* browser */ || global /* node */) as any;
myGlobal.formatAutocompleteTarget = formatAutocompleteTarget;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/taskAdd.ts